<template>
    <v-container class="form-container">
        <page-header :title="pageTitle" :back="listPath">
            <v-btn v-if="isShow" color="primary" depressed @click="goEdit">
                <v-icon left>mdi-pencil-outline</v-icon>Edit
            </v-btn>
        </page-header>

        <v-form ref="form" v-model="valid" :readonly="readOnly" @submit.prevent="onSubmit">
            <v-card outlined class="mb-4" :loading="loadingEntity">
                <v-card-text>
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <entity-search
                                v-model="voucher.member"
                                :search-fn="searchMembers"
                                :item-text="memberLabel"
                                :rules="required"
                                :readonly="readOnly"
                                label="Member *"
                                hint="Type a name or a card number"
                                prepend-icon="mdi-account-outline"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                v-model="voucher.team"
                                :items="teams"
                                :rules="required"
                                item-text="title"
                                item-value="id"
                                label="Team *"
                                prepend-inner-icon="mdi-account-group-outline"
                                :clearable="!readOnly"
                                return-object
                                outlined
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-textarea v-model.trim="voucher.description" label="Description" rows="3" auto-grow outlined />
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <v-card v-if="voucher.member" outlined class="mb-4">
                <v-card-title class="section-title">Member summary</v-card-title>
                <v-card-text>
                    <div class="summary">
                        <div><span class="label">Name</span>{{ fullName(voucher.member) }}</div>
                        <div v-if="voucher.member.birthDate"><span class="label">Birth date</span>{{ formatDate(voucher.member.birthDate) }}</div>
                        <div v-if="voucher.member.phone"><span class="label">Phone</span>{{ voucher.member.phone }}</div>
                        <div v-if="voucher.member.email"><span class="label">Email</span>{{ voucher.member.email }}</div>
                        <div><span class="label">Active cards</span>{{ activeCardNumbers(voucher.member.cards).join(', ') || '—' }}</div>
                    </div>
                </v-card-text>
            </v-card>

            <div v-if="!readOnly" class="form-actions">
                <v-btn text @click="goBack">Cancel</v-btn>
                <v-btn color="primary" depressed large type="submit" :loading="saving" :disabled="!valid || loadingEntity">
                    <v-icon left>mdi-content-save-outline</v-icon>{{ isCreate ? 'Create voucher' : 'Save changes' }}
                </v-btn>
            </div>
        </v-form>
    </v-container>
</template>

<script>
    import teamService from '@/services/team-service'
    import memberService from '@/services/member-service'
    import voucherService from '@/services/voucher-service'
    import formPage from '@/mixins/form-page'
    import PageHeader from '@/components/PageHeader'
    import EntitySearch from '@/components/EntitySearch'
    import { activeCardNumbers, entityId, formatDate, fullName, memberLabel } from '@/utils/format'

    export default {
        name: 'VoucherForm',
        components: { PageHeader, EntitySearch },
        mixins: [formPage],
        data: () => {
            return {
                entityLabel: 'Voucher',
                voucherId: null,
                voucher: {
                    description: '',
                    member: null,
                    team: null
                },
                teams: []
            }
        },
        created() {
            teamService.getTeamOptions().then(resp => {
                this.teams = resp.data.data
            })
            if (this.isCreate) {
                return
            }
            this.voucherId = parseInt(this.$route.params.voucherId)
            this.loadingEntity = true
            voucherService.getVoucherById(this.voucherId).then(resp => {
                this.voucher = { ...this.voucher, ...resp.data.voucher }
            }).catch(error => {
                this.$notify.error(error, 'Could not load the voucher')
            }).finally(() => {
                this.loadingEntity = false
            })
        },
        methods: {
            activeCardNumbers,
            formatDate,
            fullName,
            memberLabel,
            searchMembers: query => memberService.searchMembers(query),
            onSubmit() {
                if (!this.validate()) {
                    return
                }
                this.saving = true
                const payload = {
                    description: this.voucher.description,
                    member: entityId(this.voucher.member),
                    team: entityId(this.voucher.team)
                }
                const request = this.isEdit
                    ? voucherService.editVoucherById(this.voucherId, payload)
                    : voucherService.createVoucher(payload)
                request.then(() => {
                    this.$notify.success(this.isCreate ? 'Voucher created' : 'Voucher saved')
                    this.goBack()
                }).catch(error => {
                    this.$notify.error(error, 'Could not save the voucher')
                }).finally(() => {
                    this.saving = false
                })
            }
        }
    }
</script>

<style scoped>
.summary {
    display: grid;
    gap: 6px;
}

.label {
    display: inline-block;
    min-width: 120px;
    color: rgba(0, 0, 0, 0.6);
}

</style>
